import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus, Check } from "lucide-react";
import { mediaUrl, kr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Countdown } from "@/components/CampaignStrip";

export function ProductCard({ product, index = 0 }) {
  const navigate = useNavigate();
  const { addToCart, toggleFavorite, favorites, oneMore } = useApp();
  const [added, setAdded] = React.useState(false);
  const isFav = favorites.some((f) => f.id === product.id);
  const inStock = product.in_stock;

  const onAdd = (e) => {
    e.stopPropagation();
    if (!inStock) return;
    addToCart(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <motion.div
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 8) * 0.04 }}
      onClick={() => navigate(`/produkter/${product.slug}`)}
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-border/70 bg-card p-3.5 lux-shadow lux-hover hover:border-primary/40"
    >
      <div className="relative mb-3 overflow-hidden rounded-xl bg-gradient-to-br from-secondary/60 to-secondary/20 aspect-square">
        <img
          src={mediaUrl(product.main_image)}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.09]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <div className="absolute left-2 top-2 flex flex-col gap-1">
          {product.flash_ends_at ? (
            <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-black text-black shadow-sm" data-testid={`flash-badge-${product.slug}`}>⚡ FLASH -{product.discount_percent}% · <Countdown to={product.flash_ends_at} /></span>
          ) : product.on_sale && product.discount_percent > 0 && (
            <span className="rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold text-primary-foreground shadow-[0_4px_12px_-2px_rgba(236,30,121,0.5)]">
              −{product.discount_percent}%
            </span>
          )}
          {product.date_deal?.enabled && product.expiry_date && (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow-sm ${product.date_deal.kind === "stop_spild" ? "bg-emerald-600" : "bg-amber-600"}`} data-testid={`datedeal-badge-${product.slug}`}>
              {product.date_deal.kind === "stop_spild" ? "♻️ Stop spild" : "📅 Datovare"} · udløber {new Date(product.expiry_date).toLocaleDateString("da-DK", { day: "2-digit", month: "2-digit" })}
            </span>
          )}
          {product.halal && (
            <span className="rounded-full bg-emerald-500/95 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">HALAL</span>
          )}
        </div>
        {!inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm">
            <span className="rounded-full bg-destructive px-3 py-1 text-xs font-bold text-white">UDSOLGT</span>
          </div>
        )}
        {(
          <button
            data-testid={`favorite-btn-${product.slug}`}
            onClick={(e) => { e.stopPropagation(); toggleFavorite(product); }}
            className="absolute right-2 top-2 rounded-full glass p-1.5 shadow-sm ring-1 ring-black/5 transition-all hover:scale-110 hover:ring-primary/40"
          >
            <Heart className={`h-4 w-4 ${isFav ? "fill-primary text-primary" : "text-foreground/70"}`} />
          </button>
        )}
      </div>

      <div className="space-y-1">
        {product.producer && <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{product.producer}</p>}
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug tracking-tight transition-colors group-hover:text-primary">{product.name}</h3>
        <div className="flex items-end justify-between pt-1">
          <div className="flex flex-col">
            {product.on_sale && product.former_price && (
              <span className="text-xs text-muted-foreground line-through">{kr(product.former_price)}</span>
            )}
            <span className="price-tag text-base font-extrabold text-primary">{kr(product.effective_price)}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid={`one-more-btn-${product.slug}`}
              onClick={(e) => { e.stopPropagation(); if (inStock) oneMore(product, e); }}
              disabled={!inStock}
              className="gloss hidden h-9 items-center gap-0.5 whitespace-nowrap rounded-full bg-foreground px-2.5 text-[11px] font-black text-background transition-transform hover:scale-105 disabled:opacity-40 sm:flex"
              title="Bare 1 mere i kurven"
            >+ 1 MERE</button>
            <button
              data-testid={`product-card-add-to-cart-${product.slug}`}
              onClick={onAdd}
              disabled={!inStock}
              className={`gloss flex h-9 w-9 items-center justify-center rounded-full transition-all disabled:opacity-40 ${added ? "bg-candy-green" : "bg-primary hover:scale-110"}`}
            >
              {added ? <Check className="h-4 w-4 text-black" /> : <Plus className="h-4 w-4 text-primary-foreground" />}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
